import * as Sentry from '@sentry/react';
import type { QueryClient } from '@tanstack/react-query';
import { StoredAuth, StoredInviteCode } from '@/shared/utils/Stored';
import { clearMarkedTutorials } from '@/shared/utils/tutorials';
import { fetchApiRaw } from '@/modules/sanr-api';
import { FetchApiError } from '@/modules/fetch-core';
import { beginSsoSwitch } from '@/modules/sso-pkce';
import { track } from '@/shared/utils/track';
import { leaveForSso } from '@/shared/utils/sso';
import { captureException } from '@/modules/capture-exception';
import { showNotification } from '@/modules/show-notification';
import posthog from '@/shared/utils/posthog';
import type { AuthResponse, Profile } from '@/modules/sanr-api';
import { isDemoAccount } from '@/modules/demo-account';

// The invite verdict is read from the token response, before the
// (invite-gated) GET_ISSUER call, so an uninvited account never hits its
// 401.
class InviteRequiredError extends Error {
  constructor() {
    // Stryker disable next-line StringLiteral: equivalent — the error is matched by instanceof alone; its message is unobservable
    super('INVITE_REQUIRED');
    // Stryker disable next-line StringLiteral: equivalent — the error is matched by instanceof alone; its name is unobservable
    this.name = 'InviteRequiredError';
  }
}

function identifyUser(profile: Profile): void {
  // distinct_id stays the wallet address for address-carrying accounts (the
  // testers flag targets addresses); walletless accounts identify by
  // issuer id.
  posthog.identify(profile.address ?? profile.id, {
    user_id: profile.id,
    username: profile.username,
    registered_at: profile.createdAt,
    ethereum_provider: 'sso',
    is_demo: isDemoAccount(profile),
  });
  // The account id alone: the affected-user count and the match between a
  // support complaint and its error need an identifier, and the wallet
  // address is the one anyone can link to the person's funds.
  Sentry.setUser({ id: profile.id });
}

function trackLogin(profile: Profile): void {
  identifyUser(profile);
  track('auth_completed', { connector: 'sso' });
}

// Silent session restore on page load — same identify, different event, so
// auth_completed stays a true-login count.
export function trackSessionRestored(profile: Profile): void {
  identifyUser(profile);
  track('session_restored', { connector: 'sso' });
}

export function trackLogout(reason: 'user' | 'session_expired'): void {
  track('logout', { reason });
  posthog.reset();
  Sentry.setUser(null);
}

// StoredInviteCode is deliberately NOT cleared here: a network-error invite
// redemption keeps the code for retry — the invite code is cleared only on
// a deliberate logout when a real session existed (ProfileProvider's
// endSession).
export function clearStoredSession(queryClient: QueryClient): void {
  StoredAuth.set(null);
  queryClient.clear();
  clearMarkedTutorials();
}

// A redeemed code's response carries replacement tokens, written back into
// authValue.
async function applyInviteGate(authValue: AuthResponse): Promise<void> {
  if (authValue.invitedBy === null) {
    const storedCode = StoredInviteCode.get();
    if (storedCode != null && storedCode !== '') {
      try {
        const inviteResponse: AuthResponse = await fetchApiRaw(
          'POST_AUTH_INVITE',
          { signal: undefined, body: { inviteCode: storedCode } },
          authValue.accessToken,
        );
        StoredInviteCode.clear();
        track('invite_code_redeemed', {
          status: 'success',
          invite_code: storedCode,
          invited_by: inviteResponse.invitedBy ?? undefined,
        });
        authValue.accessToken = inviteResponse.accessToken;
        authValue.refreshToken = inviteResponse.refreshToken;
        authValue.invitedBy = inviteResponse.invitedBy ?? null;
      } catch (e) {
        if (
          e instanceof FetchApiError &&
          ((e.extra.status as number) === 400 ||
            (e.extra.status as number) === 404)
        ) {
          // 400 / 404 = an invalid or already-used code: permanent.
          StoredInviteCode.clear();
          track('invite_code_redeemed', {
            status: 'invalid',
            invite_code: storedCode,
          });
          showNotification('error', 'Invite code is invalid or already used');
        } else {
          // Network / server error — the code is kept for the retry.
          track('invite_code_redeemed', {
            status: 'error',
            invite_code: storedCode,
          });
          throw e;
        }
      }
    }
    if (authValue.invitedBy === null) {
      throw new InviteRequiredError();
    }
  } else {
    // A stored code is stale from a prior visit.
    StoredInviteCode.clear();
  }
}

// An uninvited outcome stores nothing (the waitlist, not a half-session)
// and surfaces through onInviteRequired with the SSO-known wallet address
// (null for email-only accounts).
export async function runSsoLogin({
  code,
  codeVerifier,
  redirectUri,
  queryClient,
  setProfile,
  setOverlayText,
  onInviteRequired,
}: {
  code: string;
  codeVerifier: string;
  redirectUri: string;
  queryClient: QueryClient;
  setProfile: (profile: Profile) => void;
  setOverlayText: (text: string | null) => void;
  onInviteRequired?: (payload: {
    address: string | null;
    ssoUsername?: string;
  }) => void;
}): Promise<'success' | 'invite_required' | 'error'> {
  return completeSsoTokenLogin({
    exchange: () =>
      fetchApiRaw('POST_SSO_EXCHANGE', {
        signal: undefined,
        body: { code, codeVerifier, redirectUri },
      }),
    queryClient,
    setProfile,
    setOverlayText,
    // Stryker disable next-line ConditionalExpression: equivalent — spreading {onInviteRequired: undefined} calls nothing either; the guard exists for exactOptionalPropertyTypes
    ...(onInviteRequired !== undefined && { onInviteRequired }),
  });
}

// When a session already existed (a switch, or a login completing in a tab
// whose sibling signed in meanwhile) the in-place identity replacement does
// the cleanup a logout would otherwise have done: the old account's query
// cache, the per-user tutorial dedupe, and the PostHog person must not
// survive into the new identity (identify without a reset would alias two
// persons). The cleanup runs only once the new profile is in hand, so every
// failure path leaves the previous session fully intact: a GET_ISSUER
// failure rolls StoredAuth back to the pre-switch value (null for a fresh
// login), and errors before the persist never touched it. Throws through —
// callers own the failure notification/tracking.
async function finishSsoSession({
  authValue,
  queryClient,
  setProfile,
  onInviteRequired,
  onSuccess,
}: {
  authValue: AuthResponse;
  queryClient: QueryClient;
  setProfile: (profile: Profile) => void;
  onInviteRequired: () => void;
  onSuccess: (profile: Profile) => void;
}): Promise<'success' | 'invite_required'> {
  const previous = StoredAuth.get();
  try {
    await applyInviteGate(authValue);
  } catch (e) {
    if (e instanceof InviteRequiredError) {
      onInviteRequired();
      return 'invite_required';
    }
    throw e;
  }
  StoredAuth.set({
    kind: 'sso',
    account: authValue.address,
    accessToken: authValue.accessToken,
    refreshToken: authValue.refreshToken,
  });
  try {
    const profileData = await fetchApiRaw(
      'GET_ISSUER',
      // Stryker disable next-line ObjectLiteral: equivalent — the slot carries only the absent abort signal, and the type forbids omitting it
      { signal: undefined },
      authValue.accessToken,
    );
    const profile: Profile = {
      ...profileData,
      accessToken: authValue.accessToken,
    };
    if (previous !== null) {
      queryClient.clear();
      clearMarkedTutorials();
      posthog.reset();
    }
    setProfile(profile);
    onSuccess(profile);
    return 'success';
  } catch (e) {
    StoredAuth.set(previous);
    throw e;
  }
}

async function completeSsoTokenLogin({
  exchange,
  queryClient,
  setProfile,
  setOverlayText,
  onInviteRequired,
}: {
  exchange: () => Promise<AuthResponse>;
  queryClient: QueryClient;
  setProfile: (profile: Profile) => void;
  setOverlayText: (text: string | null) => void;
  onInviteRequired?: (payload: {
    address: string | null;
    ssoUsername?: string;
  }) => void;
}): Promise<'success' | 'invite_required' | 'error'> {
  try {
    setOverlayText('Authenticating…');
    const authValue: AuthResponse = await exchange();
    return await finishSsoSession({
      authValue,
      queryClient,
      setProfile,
      // Not a failure — the user is simply not invited; no session was
      // stored. Surface it so the landing opens the waitlist; the username
      // rides along so the modal can name the rejected account (the issuer
      // session keeps resuming it until the user switches).
      onInviteRequired: () =>
        onInviteRequired?.({
          address: authValue.address,
          ssoUsername: authValue.username,
        }),
      onSuccess: (profile) => trackLogin(profile),
    });
  } catch (e) {
    showNotification('error', 'Something went wrong, try again');
    captureException(e);
    track('auth_failed', {
      connector: 'sso',
      reason: 'backend_error',
      ...(e instanceof FetchApiError ?
        { status: e.extra.status as number }
      : {}),
    });
    return 'error';
  } finally {
    setOverlayText(null);
  }
}

// `switched: true` carries the target account's complete token pair;
// `switched: false` hands back a prompt=none authorize URL, and the
// /auth/callback route finishes through runSsoSwitchCallback. Every failure
// keeps the current session: nothing is stored before finishSsoSession,
// and its rollback restores the pre-switch value. An uninvited target
// account is refused with the old session intact — a failed switch must
// never tear the user out of a working session into the waitlist flow.
export async function runSsoSwitch({
  targetSub,
  redirectUri,
  returnTo,
  queryClient,
  setProfile,
  setOverlayText,
}: {
  targetSub: string;
  redirectUri: string;
  returnTo: string;
  queryClient: QueryClient;
  setProfile: (profile: Profile) => void;
  setOverlayText: (text: string | null) => void;
}): Promise<'success' | 'redirect' | 'invite_required' | 'error'> {
  try {
    setOverlayText('Switching account…');
    // A vanished session (another tab's logout) rides through as a
    // token-less request — the resulting 401 lands in the shared failure
    // path below, with the same feedback as any other rejection.
    const response = await fetchApiRaw(
      'POST_AUTH_SWITCH',
      { signal: undefined, body: { targetSub, redirectUri, returnTo } },
      StoredAuth.get()?.accessToken,
    );
    if (!response.switched) {
      beginSsoSwitch({ redirectUrl: response.redirectUrl, returnTo });
      leaveForSso(response.redirectUrl);
      return 'redirect';
    }
    return await finishSsoSession({
      authValue: response,
      queryClient,
      setProfile,
      onInviteRequired: () => {
        track('account_switch_failed', { reason: 'invite_required' });
        showNotification('error', 'That account has no access to SanR yet');
      },
      onSuccess: (profile) => {
        identifyUser(profile);
        track('account_switch_completed', { method: 'instant' });
      },
    });
  } catch (e) {
    captureException(e);
    track('account_switch_failed', { reason: 'backend_error' });
    showNotification('error', 'Could not switch account, try again');
    return 'error';
  } finally {
    setOverlayText(null);
  }
}

// Bearer-less: the server-held state row and PKCE verifier authenticate
// the single-use state + code. returnTo is the server-echoed same-origin
// path from the original switch call (null when it was filtered out).
export async function runSsoSwitchCallback({
  state,
  code,
  queryClient,
  setProfile,
  setOverlayText,
}: {
  state: string;
  code: string;
  queryClient: QueryClient;
  setProfile: (profile: Profile) => void;
  setOverlayText: (text: string | null) => void;
}): Promise<{
  outcome: 'success' | 'invite_required' | 'error';
  returnTo: string | null;
}> {
  try {
    setOverlayText('Switching account…');
    const response = await fetchApiRaw('POST_AUTH_SWITCH_CALLBACK', {
      signal: undefined,
      body: { state, code },
    });
    const outcome = await finishSsoSession({
      authValue: response,
      queryClient,
      setProfile,
      onInviteRequired: () => {
        track('account_switch_failed', { reason: 'invite_required' });
        showNotification('error', 'That account has no access to SanR yet');
      },
      onSuccess: (profile) => {
        identifyUser(profile);
        track('account_switch_completed', { method: 'redirect' });
      },
    });
    return { outcome, returnTo: response.returnTo ?? null };
  } catch (e) {
    captureException(e);
    track('account_switch_failed', { reason: 'backend_error' });
    showNotification('error', 'Could not switch account, try again');
    return { outcome: 'error', returnTo: null };
  } finally {
    setOverlayText(null);
  }
}

// No queryClient.clear() here — this runs on mount before any anonymous
// queries have cached data. Clearing would cancel children's queries
// that are already paused on authReadyPromise.
export async function handleRestoreSession(): Promise<Profile | null> {
  const stored = StoredAuth.get();
  if (!stored) return null;
  return fetchProfileWithLiveToken(stored.accessToken);
}

// The GET_ISSUER may go through a transparent refresh, so the stored token
// is re-read after it instead of carrying the expired one captured before.
export async function fetchProfileWithLiveToken(
  accessToken: string,
): Promise<Profile> {
  const profileData = await fetchApiRaw(
    'GET_ISSUER',
    // Stryker disable next-line ObjectLiteral: equivalent — the slot carries only the absent abort signal, and the type forbids omitting it
    { signal: undefined },
    accessToken,
  );
  return {
    ...profileData,
    accessToken: StoredAuth.get()?.accessToken ?? accessToken,
  };
}
